import { useEffect, useMemo, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { paginateQuery } from "@/lib/paginateSupabase";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { Switch } from "@/components/ui/switch";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { useToast } from "@/hooks/use-toast";
import {
  Loader2, BookOpen, FileCheck2, Send, ShieldCheck,
  HelpCircle, FlaskConical, Building2, Users, GraduationCap, X, Info,
} from "lucide-react";
import { cn } from "@/lib/utils";

// ──────────────────────────────────────────────────────────────
// Types
// ──────────────────────────────────────────────────────────────
type Cohort = { id: string; name: string; organization_id: string | null; organizations: { name: string } | null };
type Profile = { id: string; full_name: string | null; email: string | null; organization_id: string | null };
type Org = { id: string; name: string };

type ScopeFilters = {
  org_ids: string[];
  cohort_ids: string[];
  user_ids: string[];
};

type ActionKey = 'content' | 'deliverables' | 'project_submitted' | 'project_approved' | 'project_answered' | 'lab';

const ACTIONS: { key: ActionKey; label: string; description: string; icon: React.ComponentType<any>; color: string }[] = [
  { key: 'content',           label: 'Contenido',          description: 'Marcar como vistos los contenidos obligatorios de la ruta.',                 icon: BookOpen,     color: 'text-blue-600' },
  { key: 'deliverables',      label: 'Entregables',        description: 'Aprobar los entregables de cada taller del programa.',                       icon: FileCheck2,   color: 'text-emerald-600' },
  { key: 'project_submitted', label: 'Proyecto entregado', description: 'Marcar el proyecto de los participantes como ya entregado.',                 icon: Send,         color: 'text-violet-600' },
  { key: 'project_approved',  label: 'Proyecto aprobado',  description: 'Aprobar los proyectos que ya están entregados.',                             icon: ShieldCheck,  color: 'text-amber-600' },
  { key: 'project_answered',  label: 'Preguntas del proyecto', description: 'Completar las 12 preguntas de la rúbrica LIDER-IA del proyecto.',        icon: HelpCircle,   color: 'text-pink-600' },
  { key: 'lab',               label: 'Laboratorio de Retos', description: 'Crear o publicar el reto del Laboratorio de cada participante.',           icon: FlaskConical, color: 'text-cyan-600' },
];

// Mapeo de claves técnicas del preview a etiquetas legibles
const PREVIEW_LABELS: Record<string, string> = {
  users: 'Participantes afectados',
  contents: 'Contenidos a marcar',
  total_pairs: 'Total de marcas a aplicar',
  already_completed: 'Ya completados (se omitirán)',
  to_write: 'Por marcar como completados',
  workshops: 'Talleres con entregable',
  already_validated: 'Ya validados (se omitirán)',
  projects_found: 'Proyectos encontrados',
  to_create: 'Por eximir (sin proyecto real)',
  to_update: 'Proyectos por actualizar',
  already_submitted: 'Ya entregados',
  to_approve: 'Por aprobar',
  already_approved: 'Ya aprobados',
  already_complete: 'Ya completos',
  challenges_matched: 'Retos encontrados',
  to_publish: 'Retos por publicar',
  users_to_exempt: 'Por eximir (sin reto/solución real)',
};

// ──────────────────────────────────────────────────────────────
// Reusable multi-select with search
// ──────────────────────────────────────────────────────────────
function MultiCheck({
  items, selected, onChange, getKey, getLabel, height = 220, emptyText = "Sin opciones",
}: {
  items: any[];
  selected: string[];
  onChange: (s: string[]) => void;
  getKey: (i: any) => string;
  getLabel: (i: any) => string;
  height?: number;
  emptyText?: string;
}) {
  if (items.length === 0) return <p className="text-xs text-muted-foreground">{emptyText}</p>;
  return (
    <ScrollArea className="border rounded-md p-2" style={{ height }}>
      <div className="space-y-1 pr-3">
        {items.map((it) => {
          const k = getKey(it);
          const checked = selected.includes(k);
          return (
            <label key={k} className="flex items-center gap-2 text-sm cursor-pointer hover:bg-muted/50 rounded px-1 py-0.5">
              <Checkbox
                checked={checked}
                onCheckedChange={(v) => {
                  if (v) onChange([...selected, k]);
                  else onChange(selected.filter((x) => x !== k));
                }}
              />
              <span className="truncate">{getLabel(it)}</span>
            </label>
          );
        })}
      </div>
    </ScrollArea>
  );
}

// ──────────────────────────────────────────────────────────────
// Scope global: Empresa + Cohorte + Usuarios
// ──────────────────────────────────────────────────────────────
function ScopeSelector({
  orgs, cohorts, users, participantsByCohort, scope, setScope,
}: {
  orgs: Org[];
  cohorts: Cohort[];
  users: Profile[];
  participantsByCohort: Map<string, Set<string>>;
  scope: ScopeFilters;
  setScope: (s: ScopeFilters) => void;
}) {
  const [userSearch, setUserSearch] = useState("");
  const [orgSearch, setOrgSearch] = useState("");
  const [cohortSearch, setCohortSearch] = useState("");

  // Cohortes filtradas por org seleccionada (si hay)
  const visibleCohorts = useMemo(() => {
    let list = cohorts;
    if (scope.org_ids.length > 0) {
      list = list.filter((c) => c.organization_id && scope.org_ids.includes(c.organization_id));
    }
    if (cohortSearch.trim()) {
      const q = cohortSearch.toLowerCase();
      list = list.filter((c) =>
        c.name.toLowerCase().includes(q) ||
        (c.organizations?.name ?? "").toLowerCase().includes(q)
      );
    }
    return list;
  }, [cohorts, scope.org_ids, cohortSearch]);

  // Usuarios filtrados por org/cohorte y búsqueda.
  // Mismo criterio que la edge function: intersección entre empresa y cohorte
  // (unión entre varias cohortes: participante de CUALQUIERA de las seleccionadas).
  const filteredUsers = useMemo(() => {
    let list = users;
    if (scope.org_ids.length > 0) {
      list = list.filter((u) => u.organization_id && scope.org_ids.includes(u.organization_id));
    }
    if (scope.cohort_ids.length > 0) {
      const allowed = new Set<string>();
      for (const cid of scope.cohort_ids) {
        participantsByCohort.get(cid)?.forEach((uid) => allowed.add(uid));
      }
      list = list.filter((u) => allowed.has(u.id));
    }
    if (userSearch.trim()) {
      const q = userSearch.toLowerCase();
      list = list.filter((u) =>
        (u.full_name ?? "").toLowerCase().includes(q) ||
        (u.email ?? "").toLowerCase().includes(q)
      );
    }
    return list.slice(0, 100);
  }, [users, participantsByCohort, scope.org_ids, scope.cohort_ids, userSearch]);

  const visibleOrgs = useMemo(() => {
    if (!orgSearch.trim()) return orgs;
    const q = orgSearch.toLowerCase();
    return orgs.filter((o) => o.name.toLowerCase().includes(q));
  }, [orgs, orgSearch]);

  const clearAll = () => setScope({ org_ids: [], cohort_ids: [], user_ids: [] });

  // Resumen en lenguaje natural de a quién se marcará con la selección actual.
  // La empresa es SOLO un filtro: nunca define por sí sola el alcance del marcado.
  const summary = useMemo(() => {
    if (scope.user_ids.length > 0) {
      return { tone: "individual", text: `Marcado individual: ${scope.user_ids.length} usuario(s) seleccionado(s). Solo se marcará a esas personas.` };
    }
    if (scope.cohort_ids.length > 0) {
      return { tone: "grupo", text: `Se marcará a TODOS los participantes de ${scope.cohort_ids.length} cohorte(s).` };
    }
    if (scope.org_ids.length > 0) {
      return { tone: "aviso", text: "La empresa solo filtra las cohortes y usuarios de abajo. Selecciona una cohorte (o usuarios puntuales) para poder marcar." };
    }
    return { tone: "vacio", text: "Aún no has elegido a quién marcar. Selecciona una cohorte abajo." };
  }, [scope]);

  return (
    <Card className="border-primary/30">
      <CardHeader className="pb-3">
        <div className="flex items-center justify-between">
          <div>
            <CardTitle className="text-base">¿A quién aplicar el marcado?</CardTitle>
            <CardDescription>Elige el alcance combinando los filtros de abajo.</CardDescription>
          </div>
          {(scope.org_ids.length > 0 || scope.cohort_ids.length > 0 || scope.user_ids.length > 0) && (
            <Button size="sm" variant="ghost" onClick={clearAll} className="gap-1">
              <X className="h-3 w-3" /> Limpiar
            </Button>
          )}
        </div>
      </CardHeader>
      <CardContent>
        {/* Instrucciones */}
        <div className="mb-4 rounded-lg border border-primary/20 bg-primary/5 p-3">
          <div className="flex items-center gap-2 text-sm font-medium text-foreground mb-2">
            <Info className="h-4 w-4 text-primary" /> Cómo elegir a quién marcar
          </div>
          <ul className="space-y-1.5 text-sm text-muted-foreground">
            <li className="flex gap-2">
              <GraduationCap className="h-4 w-4 mt-0.5 flex-shrink-0 text-primary/70" />
              <span>Al seleccionar una <strong className="text-foreground">cohorte</strong> se marcan <strong className="text-foreground">todos sus usuarios</strong> automáticamente. Es la forma normal de marcar a un grupo completo.</span>
            </li>
            <li className="flex gap-2">
              <Building2 className="h-4 w-4 mt-0.5 flex-shrink-0 text-primary/70" />
              <span>La <strong className="text-foreground">empresa</strong> es <strong className="text-foreground">solo un filtro</strong>: acota las cohortes y usuarios que ves abajo. No marca a nadie por sí sola — siempre debes elegir al menos una cohorte.</span>
            </li>
            <li className="flex gap-2">
              <Users className="h-4 w-4 mt-0.5 flex-shrink-0 text-primary/70" />
              <span>La sección <strong className="text-foreground">Usuarios (opcional)</strong> es <strong className="text-foreground">solo para marcado individual</strong>: úsala para elegir personas puntuales. Si seleccionas usuarios aquí, el marcado se limita a ellos. Déjala vacía para marcar a toda la cohorte.</span>
            </li>
          </ul>
        </div>

        {/* Resumen dinámico de la selección actual */}
        <div
          className={cn(
            "mb-4 rounded-md px-3 py-2 text-sm border",
            summary.tone === "vacio"
              ? "bg-muted/40 text-muted-foreground border-transparent"
              : summary.tone === "aviso"
                ? "bg-amber-50 text-amber-900 border-amber-200 dark:bg-amber-950/30 dark:text-amber-200 dark:border-amber-900"
                : summary.tone === "individual"
                  ? "bg-blue-50 text-blue-900 border-blue-200 dark:bg-blue-950/30 dark:text-blue-200 dark:border-blue-900"
                  : "bg-emerald-50 text-emerald-900 border-emerald-200 dark:bg-emerald-950/30 dark:text-emerald-200 dark:border-emerald-900"
          )}
        >
          <strong>Alcance actual:</strong> {summary.text}
        </div>

        <div className="grid md:grid-cols-3 gap-4">
          <div className="space-y-2">
            <Label className="flex items-center gap-1.5 text-sm font-medium">
              <Building2 className="h-3.5 w-3.5" /> Empresas <span className="text-[10px] font-normal text-muted-foreground">(filtro)</span>
              {scope.org_ids.length > 0 && <Badge variant="secondary" className="ml-auto text-[10px]">{scope.org_ids.length}</Badge>}
            </Label>
            <p className="text-xs text-muted-foreground -mt-1">Solo filtra las cohortes y usuarios de abajo.</p>
            <Input placeholder="Buscar empresa..." value={orgSearch} onChange={(e) => setOrgSearch(e.target.value)} className="h-8 text-sm" />
            <MultiCheck
              items={visibleOrgs}
              selected={scope.org_ids}
              onChange={(v) => setScope({ ...scope, org_ids: v })}
              getKey={(o) => o.id}
              getLabel={(o) => o.name}
            />
          </div>

          <div className="space-y-2">
            <Label className="flex items-center gap-1.5 text-sm font-medium">
              <GraduationCap className="h-3.5 w-3.5" /> Cohortes
              {scope.cohort_ids.length > 0 && <Badge variant="secondary" className="ml-auto text-[10px]">{scope.cohort_ids.length}</Badge>}
            </Label>
            <p className="text-xs text-muted-foreground -mt-1">Marca a todos los usuarios de la cohorte.</p>
            <Input placeholder={`Buscar cohorte${scope.org_ids.length > 0 ? ' (en empresas seleccionadas)' : ''}...`} value={cohortSearch} onChange={(e) => setCohortSearch(e.target.value)} className="h-8 text-sm" />
            <MultiCheck
              items={visibleCohorts}
              selected={scope.cohort_ids}
              onChange={(v) => setScope({ ...scope, cohort_ids: v })}
              getKey={(c) => c.id}
              getLabel={(c) => `${(c.organizations?.name ?? 'Sin org')} — ${c.name}`}
            />
          </div>

          <div className="space-y-2">
            <Label className="flex items-center gap-1.5 text-sm font-medium">
              <Users className="h-3.5 w-3.5" /> Usuarios (opcional)
              {scope.user_ids.length > 0 && <Badge variant="secondary" className="ml-auto text-[10px]">{scope.user_ids.length}</Badge>}
            </Label>
            <p className="text-xs text-muted-foreground -mt-1">Solo para marcado individual. Vacío = toda la cohorte.</p>
            <Input placeholder="Buscar por nombre o email..." value={userSearch} onChange={(e) => setUserSearch(e.target.value)} className="h-8 text-sm" />
            <MultiCheck
              items={filteredUsers}
              selected={scope.user_ids}
              onChange={(v) => setScope({ ...scope, user_ids: v })}
              getKey={(u) => u.id}
              getLabel={(u) => `${u.full_name ?? '(sin nombre)'} — ${u.email ?? ''}`}
            />
          </div>
        </div>
      </CardContent>
    </Card>
  );
}

// ──────────────────────────────────────────────────────────────
// Confirmation Dialog (común a todas las acciones)
// ──────────────────────────────────────────────────────────────
function ConfirmDialog({
  open, onOpenChange, title, mode, preview, onApply, loading,
}: {
  open: boolean;
  onOpenChange: (o: boolean) => void;
  title: string;
  mode?: string;
  preview: any;
  onApply: () => void;
  loading: boolean;
}) {
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>{title}</DialogTitle>
          <DialogDescription>Revisa el resumen antes de aplicar el marcado.</DialogDescription>
        </DialogHeader>
        {preview && (
          <div className="space-y-1 text-sm rounded-md border bg-muted/30 p-3">
            {Object.entries(preview).map(([k, v]) =>
              typeof v === 'number' ? (
                <p key={k} className="flex justify-between">
                  <span className="text-muted-foreground">{PREVIEW_LABELS[k] ?? k.replace(/_/g, ' ')}</span>
                  <strong>{v as number}</strong>
                </p>
              ) : null
            )}
          </div>
        )}
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>Cancelar</Button>
          <Button onClick={onApply} disabled={loading || !preview}>
            {loading && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
            Confirmar y ejecutar
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ──────────────────────────────────────────────────────────────
// Action Panels (one per action key)
// ──────────────────────────────────────────────────────────────
// La empresa (org_ids) es SOLO un filtro de UI para acotar las cohortes/usuarios
// visibles. Nunca cuenta como alcance de marcado ni se envía al edge function.
// El marcado siempre requiere una cohorte o usuarios puntuales.
const hasTarget = (s: ScopeFilters) => s.cohort_ids.length > 0 || s.user_ids.length > 0;
const markFilters = (s: ScopeFilters) => ({ cohort_ids: s.cohort_ids, user_ids: s.user_ids });

function ContentActionPanel({ scope, contentTypeOptions }: { scope: ScopeFilters; contentTypeOptions: string[] }) {
  const { toast } = useToast();
  const [contentTypes, setContentTypes] = useState<string[]>([]);
  const [onlyMandatory, setOnlyMandatory] = useState(true);
  const [loading, setLoading] = useState(false);
  const [preview, setPreview] = useState<any>(null);
  const [confirmOpen, setConfirmOpen] = useState(false);

  const run = async (op: string) => {
    setLoading(true);
    const { data, error } = await supabase.functions.invoke("admin-bulk-complete", {
      body: { op, filters: { ...markFilters(scope), content_types: contentTypes, only_mandatory: onlyMandatory } },
    });
    setLoading(false);
    if (error) { toast({ title: "Error", description: error.message, variant: "destructive" }); return null; }
    return data;
  };

  return (
    <div className="space-y-4">
      <Card>
        <CardHeader><CardTitle className="text-sm">Opciones de la acción</CardTitle></CardHeader>
        <CardContent className="space-y-4">
          <div className="space-y-2">
            <Label className="text-sm">Tipos de contenido a marcar</Label>
            <MultiCheck
              items={contentTypeOptions.map((t) => ({ id: t, name: t }))}
              selected={contentTypes}
              onChange={setContentTypes}
              getKey={(i) => i.id}
              getLabel={(i) => i.name}
              height={140}
            />
          </div>
          <div className="flex items-center gap-2">
            <Switch id="mand" checked={onlyMandatory} onCheckedChange={setOnlyMandatory} />
            <Label htmlFor="mand" className="text-sm">Solo contenidos marcados como obligatorios</Label>
          </div>
        </CardContent>
      </Card>
      <div className="flex justify-end">
        <Button
          disabled={loading || !hasTarget(scope) || contentTypes.length === 0}
          onClick={async () => { const d = await run("preview_content"); if (d) { setPreview(d); setConfirmOpen(true); } }}
        >
          {loading && <Loader2 className="w-4 h-4 mr-2 animate-spin" />} Vista previa
        </Button>
      </div>
      <ConfirmDialog
        open={confirmOpen} onOpenChange={setConfirmOpen}
        title="Confirmar marcado de contenido"
        preview={preview} loading={loading}
        onApply={async () => {
          const d = await run("apply_content"); setConfirmOpen(false);
          if (d) toast({ title: "Listo", description: `${d.written ?? 0} registros actualizados` });
          setPreview(null);
        }}
      />
    </div>
  );
}

function SimpleActionPanel({
  scope, op, title, requireCohort, sample,
}: {
  scope: ScopeFilters;
  op: { preview: string; apply: string };
  title: string;
  requireCohort?: boolean;
  sample?: string;
}) {
  const { toast } = useToast();
  const [loading, setLoading] = useState(false);
  const [preview, setPreview] = useState<any>(null);
  const [confirmOpen, setConfirmOpen] = useState(false);

  const needsCohort = requireCohort && scope.cohort_ids.length === 0;
  const disabled = loading || !hasTarget(scope) || needsCohort;

  const run = async (operation: string) => {
    setLoading(true);
    const { data, error } = await supabase.functions.invoke("admin-bulk-complete", {
      body: { op: operation, filters: markFilters(scope) },
    });
    setLoading(false);
    if (error) { toast({ title: "Error", description: error.message, variant: "destructive" }); return null; }
    return data;
  };

  return (
    <div className="space-y-4">
      {sample && (
        <Card className="border-amber-200 bg-amber-50/50 dark:bg-amber-950/20">
          <CardContent className="p-4 text-sm text-amber-900 dark:text-amber-200">
            <strong>Qué se inserta:</strong> {sample}
          </CardContent>
        </Card>
      )}
      {requireCohort && needsCohort && (
        <p className="text-sm text-destructive">Selecciona al menos una cohorte para poder ejecutar esta acción.</p>
      )}
      <div className="flex justify-end">
        <Button
          disabled={disabled}
          onClick={async () => { const d = await run(op.preview); if (d) { setPreview(d); setConfirmOpen(true); } }}
        >
          {loading && <Loader2 className="w-4 h-4 mr-2 animate-spin" />} Vista previa
        </Button>
      </div>
      <ConfirmDialog
        open={confirmOpen} onOpenChange={setConfirmOpen}
        title={`Confirmar: ${title}`}
        preview={preview} loading={loading}
        onApply={async () => {
          const d = await run(op.apply); setConfirmOpen(false);
          if (d) {
            const total =
              (d.written ?? 0) + (d.updated ?? 0) +
              (d.exempted ?? 0) + (d.approved ?? 0);
            toast({ title: "Listo", description: `${total} cambio(s) aplicado(s)` });
          }
          setPreview(null);
        }}
      />
    </div>
  );
}

function ChallengesActionPanel({ scope }: { scope: ScopeFilters }) {
  const { toast } = useToast();
  const [mode, setMode] = useState<"publish" | "complete">("complete");
  const [statuses, setStatuses] = useState<string[]>(["draft"]);
  const [loading, setLoading] = useState(false);
  const [preview, setPreview] = useState<any>(null);
  const [confirmOpen, setConfirmOpen] = useState(false);

  const run = async (op: string) => {
    setLoading(true);
    const filters: any = { ...markFilters(scope) };
    if (mode === "publish") filters.statuses = statuses;
    const { data, error } = await supabase.functions.invoke("admin-bulk-complete", { body: { op, mode, filters } });
    setLoading(false);
    if (error) { toast({ title: "Error", description: error.message, variant: "destructive" }); return null; }
    return data;
  };

  return (
    <div className="space-y-4">
      <Card>
        <CardHeader><CardTitle className="text-sm">Modo</CardTitle></CardHeader>
        <CardContent className="space-y-3">
          <Select value={mode} onValueChange={(v) => setMode(v as any)}>
            <SelectTrigger><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="complete">Eximir del requisito a quien no tenga reto y solución reales</SelectItem>
              <SelectItem value="publish">Solo publicar los retos ya creados</SelectItem>
            </SelectContent>
          </Select>
          <p className="text-xs text-muted-foreground">
            {mode === "publish"
              ? "Cambia los retos del Laboratorio existentes a estado “publicado” (activo). No crea retos nuevos ni soluciones."
              : "Para cada participante que no tenga un reto con solución real, se le exime del requisito de Laboratorio para efectos de certificación — no se crea ningún reto ni solución falsos."}
          </p>
          {mode === "publish" && (
            <div className="space-y-2 pt-2">
              <Label className="text-sm">Filtrar por estado actual del reto</Label>
              <MultiCheck
                items={[
                  { id: 'draft',     name: 'Borrador' },
                  { id: 'active',    name: 'Publicado' },
                  { id: 'completed', name: 'Completado' },
                ]}
                selected={statuses}
                onChange={setStatuses}
                getKey={(i) => i.id}
                getLabel={(i) => i.name}
                height={120}
              />
            </div>
          )}
        </CardContent>
      </Card>
      <div className="flex justify-end">
        <Button
          disabled={loading || !hasTarget(scope)}
          onClick={async () => { const d = await run("preview_challenges"); if (d) { setPreview(d); setConfirmOpen(true); } }}
        >
          {loading && <Loader2 className="w-4 h-4 mr-2 animate-spin" />} Vista previa
        </Button>
      </div>
      <ConfirmDialog
        open={confirmOpen} onOpenChange={setConfirmOpen}
        title={mode === "publish" ? "Confirmar: publicar retos" : "Confirmar: completar retos"}
        preview={preview} loading={loading}
        onApply={async () => {
          const op = mode === "publish" ? "apply_publish_challenges" : "apply_complete_challenges";
          const d = await run(op); setConfirmOpen(false);
          if (d) {
            const msg = mode === "publish"
              ? `${d.updated ?? 0} retos publicados`
              : `${d.exempted ?? 0} participante(s) eximidos del requisito de laboratorio`;
            toast({ title: "Listo", description: msg });
          }
          setPreview(null);
        }}
      />
    </div>
  );
}

// ──────────────────────────────────────────────────────────────
// Sidebar item
// ──────────────────────────────────────────────────────────────
function SidebarItem({ action, active, onClick }: { action: typeof ACTIONS[number]; active: boolean; onClick: () => void }) {
  const Icon = action.icon;
  return (
    <button
      type="button"
      onClick={onClick}
      className={cn(
        "w-full text-left rounded-lg p-3 transition-colors border",
        active
          ? "bg-primary/10 border-primary/40 text-foreground"
          : "border-transparent hover:bg-muted/50 text-muted-foreground hover:text-foreground"
      )}
    >
      <div className="flex items-start gap-2.5">
        <Icon className={cn("h-5 w-5 mt-0.5 flex-shrink-0", active ? action.color : "")} />
        <div className="min-w-0">
          <p className="font-medium text-sm">{action.label}</p>
          <p className="text-xs text-muted-foreground line-clamp-2">{action.description}</p>
        </div>
      </div>
    </button>
  );
}

// ──────────────────────────────────────────────────────────────
// Main page
// ──────────────────────────────────────────────────────────────
export default function BulkCompletionPage() {
  const [orgs, setOrgs] = useState<Org[]>([]);
  const [cohorts, setCohorts] = useState<Cohort[]>([]);
  const [users, setUsers] = useState<Profile[]>([]);
  const [participantsByCohort, setParticipantsByCohort] = useState<Map<string, Set<string>>>(new Map());
  const [contentTypes, setContentTypes] = useState<string[]>([]);
  const [loading, setLoading] = useState(true);

  const [scope, setScope] = useState<ScopeFilters>({ org_ids: [], cohort_ids: [], user_ids: [] });
  const [activeAction, setActiveAction] = useState<ActionKey>('content');

  useEffect(() => {
    (async () => {
      const [orgsRes, cohortsRes, usersData, participantsData, ctData] = await Promise.all([
        supabase.from('organizations').select('id, name').order('name'),
        supabase.from("cohorts").select("id, name, organization_id, organizations(name)").order("name"),
        paginateQuery<Profile>((from, to) =>
          supabase.from("profiles").select("id, full_name, email, organization_id").order("full_name").range(from, to)
        ),
        paginateQuery<{ user_id: string; cohort_id: string }>((from, to) =>
          supabase.from("cohort_participants").select("user_id, cohort_id").range(from, to)
        ),
        paginateQuery<{ content_type: string }>((from, to) =>
          supabase.from("content_bank").select("content_type").eq("planning_status", "published").range(from, to)
        ),
      ]);
      setOrgs((orgsRes.data ?? []) as Org[]);
      const sorted = ((cohortsRes.data ?? []) as any[]).sort((a, b) => {
        const orgA = (a.organizations?.name || 'zzz Sin org').toLowerCase();
        const orgB = (b.organizations?.name || 'zzz Sin org').toLowerCase();
        if (orgA !== orgB) return orgA.localeCompare(orgB);
        return (a.name || '').localeCompare(b.name || '');
      });
      setCohorts(sorted as any);
      setUsers(usersData);
      const byCohort = new Map<string, Set<string>>();
      for (const p of participantsData ?? []) {
        if (!p.cohort_id || !p.user_id) continue;
        let set = byCohort.get(p.cohort_id);
        if (!set) { set = new Set(); byCohort.set(p.cohort_id, set); }
        set.add(p.user_id);
      }
      setParticipantsByCohort(byCohort);
      const uniqueTypes = Array.from(new Set((ctData ?? []).map((r) => r.content_type).filter((t): t is string => !!t))).sort((a, b) => a.localeCompare(b));
      setContentTypes(uniqueTypes);
      setLoading(false);
    })();
  }, []);

  if (loading) {
    return <div className="flex items-center justify-center py-20"><Loader2 className="w-6 h-6 animate-spin" /></div>;
  }

  const current = ACTIONS.find((a) => a.key === activeAction)!;
  const Icon = current.icon;

  return (
    <div className="container mx-auto p-6 space-y-6">
      <div>
        <h1 className="text-2xl font-bold">Marcado masivo de progreso</h1>
        <p className="text-sm text-muted-foreground">
          Super admin: marca en bloque los requisitos del Diploma A — contenido, entregables, proyecto, preguntas LIDER-IA y lab.
        </p>
      </div>

      <ScopeSelector orgs={orgs} cohorts={cohorts} users={users} participantsByCohort={participantsByCohort} scope={scope} setScope={setScope} />

      <div className="grid lg:grid-cols-[280px_1fr] gap-6">
        {/* Sidebar */}
        <aside className="space-y-1.5 lg:sticky lg:top-4 lg:self-start">
          <p className="text-xs uppercase tracking-wide font-semibold text-muted-foreground mb-2 px-2">Acción</p>
          {ACTIONS.map((a) => (
            <SidebarItem key={a.key} action={a} active={activeAction === a.key} onClick={() => setActiveAction(a.key)} />
          ))}
        </aside>

        {/* Panel */}
        <section>
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Icon className={cn("h-5 w-5", current.color)} />
                {current.label}
              </CardTitle>
              <CardDescription>{current.description}</CardDescription>
            </CardHeader>
            <CardContent>
              {activeAction === 'content' && <ContentActionPanel scope={scope} contentTypeOptions={contentTypes} />}
              {activeAction === 'deliverables' && (
                <SimpleActionPanel
                  scope={scope}
                  op={{ preview: 'preview_deliverables', apply: 'apply_deliverables' }}
                  title="entregables validados"
                  requireCohort
                  sample="Marca como validados todos los entregables reales que el participante ya subió. Si no había subido uno, se le exime del requisito para efectos de certificación — no se crea ningún entregable falso."
                />
              )}
              {activeAction === 'project_submitted' && (
                <SimpleActionPanel
                  scope={scope}
                  op={{ preview: 'preview_project_submitted', apply: 'apply_project_submitted' }}
                  title="proyecto entregado"
                  requireCohort
                  sample="Marca como entregado el proyecto real del participante en su cohorte. Si todavía no tiene un proyecto creado, se le exime del requisito para efectos de certificación — no se crea ningún proyecto falso."
                />
              )}
              {activeAction === 'project_approved' && (
                <SimpleActionPanel
                  scope={scope}
                  op={{ preview: 'preview_project_approved', apply: 'apply_project_approved' }}
                  title="proyecto aprobado"
                  sample="Aprueba los proyectos que ya están entregados. Si un participante todavía no ha entregado proyecto, se omite (no se crea uno). La aprobación queda registrada con tu usuario como aprobador y la fecha actual."
                />
              )}
              {activeAction === 'project_answered' && (
                <SimpleActionPanel
                  scope={scope}
                  op={{ preview: 'preview_project_answered', apply: 'apply_project_answered' }}
                  title="preguntas del proyecto"
                  sample="Completa las 12 preguntas de la rúbrica LIDER-IA del proyecto con respuestas genéricas, solo para los participantes que las tienen vacías o sin desarrollar. Las respuestas que el participante ya haya escrito (con suficiente detalle) NO se reemplazan."
                />
              )}
              {activeAction === 'lab' && <ChallengesActionPanel scope={scope} />}
            </CardContent>
          </Card>
        </section>
      </div>
    </div>
  );
}
